import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '../components/ui/alert-dialog';
import { toast } from 'sonner';
import { 
  UserPlus, 
  Edit, 
  Trash2, 
  Shield, 
  Eye,
  EyeOff,
  Users,
  Factory,
  ShoppingCart,
  Truck,
  Wallet,
  FileText,
  TrendingUp,
  Check,
  X,
  Clock,
  Calendar,
  Bell,
  Euro,
  Coffee
} from 'lucide-react';
import axios from 'axios';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';

// Permission categories with icons and labels
const permissionCategories = [
  {
    title: 'الإنتاج',
    icon: Factory,
    permissions: [
      { key: 'view_production', label: 'عرض كمية الإنتاج' },
      { key: 'edit_production', label: 'إضافة/تعديل كمية الإنتاج' },
      { key: 'view_production_report', label: 'عرض تقرير الإنتاج' },
    ]
  },
  {
    title: 'الزبائن',
    icon: Users,
    permissions: [
      { key: 'view_customers', label: 'عرض الزبائن' },
      { key: 'edit_customers', label: 'إضافة/تعديل الزبائن' },
      { key: 'delete_customers', label: 'حذف الزبائن' },
      { key: 'view_customer_statement', label: 'عرض كشف حساب الزبون' },
    ]
  },
  {
    title: 'الطلبات',
    icon: ShoppingCart,
    permissions: [
      { key: 'view_orders', label: 'عرض الطلبات' },
      { key: 'edit_orders', label: 'إضافة/تعديل الطلبات' },
      { key: 'delete_orders', label: 'حذف الطلبات' },
    ]
  },
  {
    title: 'التوصيل',
    icon: Truck,
    permissions: [
      { key: 'view_delivery', label: 'عرض ورقة التوصيل' },
      { key: 'edit_delivery_status', label: 'تعديل حالة التوصيل' },
    ]
  },
  {
    title: 'المالية',
    icon: Wallet,
    permissions: [
      { key: 'view_expenses', label: 'عرض المصروفات' },
      { key: 'edit_expenses', label: 'إضافة/تعديل المصروفات' },
      { key: 'view_financial_dues', label: 'عرض المستحقات المالية' },
      { key: 'edit_financial_dues', label: 'إضافة/تعديل المستحقات' },
      { key: 'record_payments', label: 'تسجيل الدفعات' },
    ]
  },
  {
    title: 'الفواتير',
    icon: FileText,
    permissions: [
      { key: 'view_invoices', label: 'عرض الفواتير الألمانية' },
      { key: 'edit_invoices', label: 'إنشاء/تعديل الفواتير' },
    ]
  },
  {
    title: 'التقارير',
    icon: TrendingUp,
    permissions: [
      { key: 'view_profit_report', label: 'عرض تقرير الأرباح' },
      { key: 'view_attendance', label: 'عرض الدوام وتقرير الحضور' },
    ]
  },
];

// Default empty permissions
const defaultPermissions = {
  view_production: false,
  edit_production: false,
  view_production_report: false,
  view_customers: false,
  edit_customers: false,
  delete_customers: false,
  view_customer_statement: false,
  view_orders: false,
  edit_orders: false,
  delete_orders: false,
  view_delivery: false,
  edit_delivery_status: false,
  view_expenses: false,
  edit_expenses: false,
  view_financial_dues: false,
  edit_financial_dues: false,
  record_payments: false,
  view_invoices: false,
  edit_invoices: false,
  view_profit_report: false,
  view_attendance: false,
};

const StaffUsersPage = () => {
  const { t } = useLanguage();
  const { API_URL, getAuthHeader } = useAuth();
  const [staffUsers, setStaffUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [scheduleDialogOpen, setScheduleDialogOpen] = useState(false);
  const [selectedUserForSchedule, setSelectedUserForSchedule] = useState(null);
  const [scheduleForm, setScheduleForm] = useState({
    day: 'sunday',
    start_time: '08:00',
    end_time: '16:00',
    notes: ''
  });
  
  // Tomorrow's shift scheduling
  const [tomorrowShifts, setTomorrowShifts] = useState([]);
  const [shiftDialogOpen, setShiftDialogOpen] = useState(false);
  const [selectedUserForShift, setSelectedUserForShift] = useState(null);
  const [shiftForm, setShiftForm] = useState({
    start_time: '08:00',
    end_time: '16:00',
    notes: ''
  });
  
  // Today's attendance state
  const [todayAttendance, setTodayAttendance] = useState([]);
  const [attendanceLoading, setAttendanceLoading] = useState({});
  
  // Get tomorrow's date
  const getTomorrowDate = () => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    return tomorrow.toISOString().split('T')[0];
  };
  
  // Days of week for scheduling
  const daysOfWeek = [
    { value: 'sunday', label: 'الأحد' },
    { value: 'monday', label: 'الاثنين' },
    { value: 'tuesday', label: 'الثلاثاء' },
    { value: 'wednesday', label: 'الأربعاء' },
    { value: 'thursday', label: 'الخميس' },
    { value: 'friday', label: 'الجمعة' },
    { value: 'saturday', label: 'السبت' },
  ];
  
  // Form state
  const [formData, setFormData] = useState({
    username: '',
    password: '',
    name: '',
    hourly_rate: 0,
    permissions: { ...defaultPermissions }
  });
  
  // Wages dialog
  const [wagesDialogOpen, setWagesDialogOpen] = useState(false);
  const [wagesData, setWagesData] = useState(null);
  const [wagesLoading, setWagesLoading] = useState(false);
  const [wagesMonth, setWagesMonth] = useState(new Date().getMonth() + 1);
  const [wagesYear, setWagesYear] = useState(new Date().getFullYear());

  useEffect(() => {
    fetchStaffUsers();
    fetchTomorrowShifts();
    fetchTodayAttendance();
  }, []);

  const fetchStaffUsers = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/staff-users`, {
        headers: getAuthHeader()
      });
      setStaffUsers(response.data);
    } catch (error) {
      toast.error('حدث خطأ في تحميل المستخدمين');
    } finally {
      setLoading(false);
    }
  };
  
  // Fetch today's attendance for all staff
  const fetchTodayAttendance = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/attendance/today-all`, {
        headers: getAuthHeader()
      });
      setTodayAttendance(response.data || []);
    } catch (error) {
      console.error('Error fetching today attendance:', error);
    }
  };
  
  // Get attendance status for a staff member
  const getStaffAttendance = (staffId) => {
    return todayAttendance.find(a => a.staff_id === staffId);
  };
  
  // Admin clock in for staff
  const handleAdminClockIn = async (staffId) => {
    setAttendanceLoading(prev => ({ ...prev, [staffId]: 'in' }));
    try {
      await axios.post(`${API_URL}/api/attendance/admin-clock-in/${staffId}`, {}, {
        headers: getAuthHeader()
      });
      toast.success('تم تسجيل بدء الدوام');
      fetchTodayAttendance();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'حدث خطأ');
    } finally {
      setAttendanceLoading(prev => ({ ...prev, [staffId]: null }));
    }
  };
  
  // Admin clock out for staff
  const handleAdminClockOut = async (staffId) => {
    setAttendanceLoading(prev => ({ ...prev, [staffId]: 'out' }));
    try {
      await axios.post(`${API_URL}/api/attendance/admin-clock-out/${staffId}`, {}, {
        headers: getAuthHeader()
      });
      toast.success('تم تسجيل انتهاء الدوام');
      fetchTodayAttendance();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'حدث خطأ');
    } finally {
      setAttendanceLoading(prev => ({ ...prev, [staffId]: null }));
    }
  };
  
  // Fetch staff wages
  const fetchWages = async () => {
    setWagesLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/attendance/staff-wages?month=${wagesMonth}&year=${wagesYear}`, {
        headers: getAuthHeader()
      });
      setWagesData(response.data);
    } catch (error) {
      toast.error('حدث خطأ في تحميل الأجور');
    } finally {
      setWagesLoading(false);
    }
  };
  
  const openWagesDialog = () => {
    setWagesDialogOpen(true);
    fetchWages();
  };

  const fetchTomorrowShifts = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/daily-shifts/tomorrow`, {
        headers: getAuthHeader()
      });
      setTomorrowShifts(response.data.shifts || []);
    } catch (error) {
      console.error('Error fetching tomorrow shifts:', error);
    }
  };

  // Open shift dialog for a user
  const openShiftDialog = (user) => {
    setSelectedUserForShift(user);
    // Check if user already has a shift for tomorrow
    const existingShift = tomorrowShifts.find(s => s.staff_id === user.id);
    if (existingShift) {
      setShiftForm({
        start_time: existingShift.start_time,
        end_time: existingShift.end_time,
        notes: existingShift.notes || ''
      });
    } else {
      setShiftForm({
        start_time: '08:00',
        end_time: '16:00',
        notes: ''
      });
    }
    setShiftDialogOpen(true);
  };

  // Save tomorrow's shift
  const handleSaveShift = async () => {
    if (!selectedUserForShift) return;
    
    try {
      await axios.post(
        `${API_URL}/api/daily-shifts`,
        {
          staff_id: selectedUserForShift.id,
          date: getTomorrowDate(),
          start_time: shiftForm.start_time,
          end_time: shiftForm.end_time,
          notes: shiftForm.notes
        },
        { headers: getAuthHeader() }
      );
      toast.success('تم حفظ دوام الغد وإرسال إشعار للموظف');
      setShiftDialogOpen(false);
      fetchTomorrowShifts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'حدث خطأ');
    }
  };

  // Delete tomorrow's shift
  const handleDeleteShift = async (shiftId) => {
    try {
      await axios.delete(`${API_URL}/api/daily-shifts/${shiftId}`, {
        headers: getAuthHeader()
      });
      toast.success('تم حذف الدوام');
      fetchTomorrowShifts();
    } catch (error) {
      toast.error('حدث خطأ في الحذف');
    }
  };

  // Get shift for a staff member
  const getStaffTomorrowShift = (staffId) => {
    return tomorrowShifts.find(s => s.staff_id === staffId);
  };

  const handleOpenDialog = (user = null) => {
    if (user) {
      setEditingUser(user);
      setFormData({
        username: user.username,
        password: '',
        name: user.name,
        hourly_rate: user.hourly_rate || 0,
        permissions: user.permissions || { ...defaultPermissions }
      });
    } else {
      setEditingUser(null);
      setFormData({
        username: '',
        password: '',
        name: '',
        hourly_rate: 0,
        permissions: { ...defaultPermissions }
      });
    }
    setDialogOpen(true);
  };

  const handlePermissionChange = (key, value) => {
    setFormData(prev => ({
      ...prev,
      permissions: {
        ...prev.permissions,
        [key]: value
      }
    }));
  };

  const handleSelectAllCategory = (category, selected) => {
    const updates = {};
    category.permissions.forEach(p => {
      updates[p.key] = selected;
    });
    setFormData(prev => ({
      ...prev,
      permissions: {
        ...prev.permissions,
        ...updates
      }
    }));
  };

  const handleSelectAll = (selected) => {
    const updates = {};
    Object.keys(defaultPermissions).forEach(key => {
      updates[key] = selected;
    });
    setFormData(prev => ({
      ...prev,
      permissions: updates
    }));
  };

  const handleSave = async () => {
    if (!formData.name.trim()) {
      toast.error('يرجى إدخال اسم المستخدم');
      return;
    }
    if (!editingUser && !formData.username.trim()) {
      toast.error('يرجى إدخال اسم تسجيل الدخول');
      return;
    }
    if (!editingUser && !formData.password) {
      toast.error('يرجى إدخال كلمة المرور');
      return;
    }

    try {
      if (editingUser) {
        const updateData = {
          name: formData.name,
          permissions: formData.permissions
        };
        if (formData.password) {
          updateData.password = formData.password;
        }
        await axios.put(
          `${API_URL}/api/staff-users/${editingUser.id}`,
          updateData,
          { headers: getAuthHeader() }
        );
        toast.success('تم تحديث المستخدم بنجاح');
      } else {
        await axios.post(
          `${API_URL}/api/staff-users`,
          formData,
          { headers: getAuthHeader() }
        );
        toast.success('تم إضافة المستخدم بنجاح');
      }
      setDialogOpen(false);
      fetchStaffUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'حدث خطأ');
    }
  };

  const handleToggleActive = async (user) => {
    try {
      await axios.put(
        `${API_URL}/api/staff-users/${user.id}`,
        { is_active: !user.is_active },
        { headers: getAuthHeader() }
      );
      toast.success(user.is_active ? 'تم تعطيل المستخدم' : 'تم تفعيل المستخدم');
      fetchStaffUsers();
    } catch (error) {
      toast.error('حدث خطأ');
    }
  };

  const handleDelete = async (userId) => {
    try {
      await axios.delete(`${API_URL}/api/staff-users/${userId}`, {
        headers: getAuthHeader()
      });
      toast.success('تم حذف المستخدم');
      fetchStaffUsers();
    } catch (error) {
      toast.error('حدث خطأ في الحذف');
    }
  };

  // Schedule Management
  const openScheduleDialog = (user) => {
    setSelectedUserForSchedule(user);
    setScheduleForm({
      day: 'sunday',
      start_time: '08:00',
      end_time: '16:00',
      notes: ''
    });
    setScheduleDialogOpen(true);
  };

  const handleAddSchedule = async () => {
    if (!selectedUserForSchedule) return;
    
    try {
      await axios.post(
        `${API_URL}/api/staff-users/${selectedUserForSchedule.id}/schedule`,
        scheduleForm,
        { headers: getAuthHeader() }
      );
      toast.success('تم إضافة الدوام');
      fetchStaffUsers();
      setScheduleDialogOpen(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'حدث خطأ');
    }
  };

  const handleDeleteSchedule = async (userId, scheduleId) => {
    try {
      await axios.delete(
        `${API_URL}/api/staff-users/${userId}/schedule/${scheduleId}`,
        { headers: getAuthHeader() }
      );
      toast.success('تم حذف الدوام');
      fetchStaffUsers();
    } catch (error) {
      toast.error('حدث خطأ في الحذف');
    }
  };

  const getDayLabel = (dayValue) => {
    const day = daysOfWeek.find(d => d.value === dayValue);
    return day ? day.label : dayValue;
  };

  const countPermissions = (permissions) => {
    if (!permissions) return 0;
    return Object.values(permissions).filter(Boolean).length;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="staff-users-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-stone-900 flex items-center gap-2">
            <Shield className="w-7 h-7 text-amber-600" />
            إدارة المستخدمين والصلاحيات
          </h1>
          <p className="text-stone-500 mt-1">أضف موظفين وحدد صلاحياتهم في النظام</p>
        </div>
        
        <div className="flex gap-2">
          <Button
            onClick={openWagesDialog}
            variant="outline"
            className="border-green-300 text-green-700 hover:bg-green-50"
            data-testid="wages-btn"
          >
            <Euro className="w-4 h-4 ml-2" />
            حساب الأجور
          </Button>
          
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button 
                onClick={() => handleOpenDialog()}
                className="bg-amber-600 hover:bg-amber-700"
                data-testid="add-staff-btn"
              >
                <UserPlus className="w-4 h-4 ml-2" />
                إضافة مستخدم
              </Button>
            </DialogTrigger>
          </Dialog>
        </div>
      </div>

      {/* Add/Edit User Dialog - Outside the header */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>
                {editingUser ? 'تعديل مستخدم' : 'إضافة مستخدم جديد'}
              </DialogTitle>
            </DialogHeader>
            
            <div className="space-y-6 py-4">
              {/* Basic Info */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>الاسم *</Label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="اسم الموظف"
                    data-testid="staff-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>اسم تسجيل الدخول *</Label>
                  <Input
                    value={formData.username}
                    onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                    placeholder="username"
                    disabled={!!editingUser}
                    data-testid="staff-username-input"
                  />
                </div>
              </div>
              
              <div className="space-y-2">
                <Label>{editingUser ? 'كلمة المرور الجديدة (اتركها فارغة للإبقاء على القديمة)' : 'كلمة المرور *'}</Label>
                <div className="relative">
                  <Input
                    type={showPassword ? 'text' : 'password'}
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    placeholder="********"
                    data-testid="staff-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400 hover:text-stone-600"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              {/* Hourly Rate */}
              <div className="space-y-2">
                <Label>الأجر بالساعة (€)</Label>
                <div className="relative">
                  <Input
                    type="number"
                    min="0"
                    step="0.5"
                    value={formData.hourly_rate}
                    onChange={(e) => setFormData({ ...formData, hourly_rate: parseFloat(e.target.value) || 0 })}
                    placeholder="0"
                    className="text-left"
                    dir="ltr"
                    data-testid="staff-hourly-rate-input"
                  />
                  <span className="absolute left-10 top-1/2 -translate-y-1/2 text-stone-400">€/ساعة</span>
                </div>
              </div>

              {/* Permissions */}
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <Label className="text-lg font-semibold">الصلاحيات</Label>
                  <div className="flex gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => handleSelectAll(true)}
                    >
                      <Check className="w-4 h-4 ml-1" />
                      تحديد الكل
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => handleSelectAll(false)}
                    >
                      <X className="w-4 h-4 ml-1" />
                      إلغاء الكل
                    </Button>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {permissionCategories.map((category) => {
                    const Icon = category.icon;
                    const allSelected = category.permissions.every(p => formData.permissions[p.key]);
                    
                    return (
                      <Card key={category.title} className="overflow-hidden">
                        <CardHeader className="py-3 px-4 bg-stone-50 border-b">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <Icon className="w-4 h-4 text-amber-600" />
                              <span className="font-medium text-sm">{category.title}</span>
                            </div>
                            <Switch
                              checked={allSelected}
                              onCheckedChange={(checked) => handleSelectAllCategory(category, checked)}
                            />
                          </div>
                        </CardHeader>
                        <CardContent className="p-3 space-y-2">
                          {category.permissions.map((permission) => (
                            <div 
                              key={permission.key}
                              className="flex items-center justify-between py-1"
                            >
                              <span className="text-sm text-stone-600">{permission.label}</span>
                              <Switch
                                checked={formData.permissions[permission.key] || false}
                                onCheckedChange={(checked) => handlePermissionChange(permission.key, checked)}
                                data-testid={`permission-${permission.key}`}
                              />
                            </div>
                          ))}
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              </div>
            </div>

            <div className="flex justify-end gap-2 pt-4 border-t">
              <Button variant="outline" onClick={() => setDialogOpen(false)}>
                إلغاء
              </Button>
              <Button onClick={handleSave} className="bg-amber-600 hover:bg-amber-700">
                {editingUser ? 'حفظ التعديلات' : 'إضافة المستخدم'}
              </Button>
            </div>
          </DialogContent>
        </Dialog>

      {/* Tomorrow's Shift Schedule Section */}
      <Card className="border-2 border-amber-200 bg-amber-50/50">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <Calendar className="w-5 h-5 text-amber-600" />
            جدول دوام الغد ({getTomorrowDate()})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {staffUsers.length === 0 ? (
            <p className="text-center text-stone-500 py-4">أضف موظفين أولاً لتحديد دوامهم</p>
          ) : (
            <div className="space-y-3">
              {staffUsers.map((user) => {
                const shift = getStaffTomorrowShift(user.id);
                return (
                  <div 
                    key={user.id}
                    className="flex items-center justify-between p-3 bg-white rounded-lg border"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center">
                        <Users className="w-5 h-5 text-amber-600" />
                      </div>
                      <div>
                        <p className="font-medium">{user.name}</p>
                        <p className="text-xs text-stone-500">@{user.username}</p>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-3">
                      {shift ? (
                        <>
                          <div className="text-center bg-green-50 px-3 py-1 rounded-lg">
                            <span className="text-green-700 font-mono font-bold">
                              {shift.start_time} - {shift.end_time}
                            </span>
                          </div>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => openShiftDialog(user)}
                            className="text-amber-600"
                          >
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDeleteShift(shift.id)}
                            className="text-red-600"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </>
                      ) : (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => openShiftDialog(user)}
                          className="gap-2"
                        >
                          <Clock className="w-4 h-4" />
                          تحديد الدوام
                        </Button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Today's Attendance Card */}
      <Card className="border-green-200 bg-green-50/50">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-lg text-green-800">
            <Check className="w-5 h-5" />
            تسجيل حضور اليوم
            <Badge variant="secondary" className="bg-green-100 text-green-700">
              {new Date().toLocaleDateString('ar', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
            </Badge>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {staffUsers.length === 0 ? (
            <p className="text-center text-stone-500 py-4">أضف موظفين أولاً</p>
          ) : (
            <div className="space-y-3">
              {staffUsers.map((user) => {
                const attendance = getStaffAttendance(user.id);
                const isLoading = attendanceLoading[user.id];
                const isClockedIn = attendance?.clock_in && !attendance?.clock_out;
                const hasCompleted = attendance?.clock_out;
                
                return (
                  <div 
                    key={user.id}
                    className={`flex items-center justify-between p-3 rounded-lg border ${
                      hasCompleted ? 'bg-blue-50 border-blue-200' : 
                      isClockedIn ? 'bg-green-50 border-green-200' : 
                      'bg-white border-stone-200'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                        hasCompleted ? 'bg-blue-100' : isClockedIn ? 'bg-green-100' : 'bg-stone-100'
                      }`}>
                        <Users className={`w-5 h-5 ${
                          hasCompleted ? 'text-blue-600' : isClockedIn ? 'text-green-600' : 'text-stone-600'
                        }`} />
                      </div>
                      <div>
                        <p className="font-medium">{user.name}</p>
                        {attendance && (
                          <p className="text-xs text-stone-500">
                            {attendance.clock_in && `دخول: ${new Date(attendance.clock_in).toLocaleTimeString('ar', { hour: '2-digit', minute: '2-digit' })}`}
                            {attendance.clock_out && ` | خروج: ${new Date(attendance.clock_out).toLocaleTimeString('ar', { hour: '2-digit', minute: '2-digit' })}`}
                            {attendance.total_hours && ` (${attendance.total_hours.toFixed(1)} ساعة)`}
                          </p>
                        )}
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-2">
                      {hasCompleted ? (
                        <Badge className="bg-blue-600">أكمل الدوام</Badge>
                      ) : isClockedIn ? (
                        <Button
                          size="sm"
                          variant="destructive"
                          onClick={() => handleAdminClockOut(user.id)}
                          disabled={isLoading === 'out'}
                          className="gap-2"
                        >
                          {isLoading === 'out' ? (
                            <span className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full" />
                          ) : (
                            <X className="w-4 h-4" />
                          )}
                          إنهاء الدوام
                        </Button>
                      ) : (
                        <Button
                          size="sm"
                          onClick={() => handleAdminClockIn(user.id)}
                          disabled={isLoading === 'in'}
                          className="gap-2 bg-green-600 hover:bg-green-700"
                        >
                          {isLoading === 'in' ? (
                            <span className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full" />
                          ) : (
                            <Check className="w-4 h-4" />
                          )}
                          بدء الدوام
                        </Button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Tomorrow Shift Dialog */}
      <Dialog open={shiftDialogOpen} onOpenChange={setShiftDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Calendar className="w-5 h-5 text-amber-600" />
              تحديد دوام الغد
              {selectedUserForShift && (
                <span className="text-stone-500 font-normal text-sm">- {selectedUserForShift.name}</span>
              )}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="bg-amber-50 p-3 rounded-lg text-center">
              <p className="text-sm text-amber-700">
                تاريخ الدوام: <strong>{getTomorrowDate()}</strong>
              </p>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>وقت البدء</Label>
                <Input
                  type="time"
                  value={shiftForm.start_time}
                  onChange={(e) => setShiftForm({...shiftForm, start_time: e.target.value})}
                />
              </div>
              <div className="space-y-2">
                <Label>وقت الانتهاء</Label>
                <Input
                  type="time"
                  value={shiftForm.end_time}
                  onChange={(e) => setShiftForm({...shiftForm, end_time: e.target.value})}
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label>ملاحظات (اختياري)</Label>
              <Input
                value={shiftForm.notes}
                onChange={(e) => setShiftForm({...shiftForm, notes: e.target.value})}
                placeholder="مثال: دوام صباحي"
              />
            </div>
          </div>
          
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setShiftDialogOpen(false)}>إلغاء</Button>
            <Button onClick={handleSaveShift} className="bg-amber-600 hover:bg-amber-700 gap-2">
              <Bell className="w-4 h-4" />
              حفظ وإرسال إشعار
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Users List */}
      {staffUsers.length === 0 ? (
        <Card className="text-center py-12">
          <Users className="w-16 h-16 text-stone-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-stone-600">لا يوجد مستخدمين</h3>
          <p className="text-stone-400 mt-1">أضف مستخدمين لمنحهم صلاحيات محددة في النظام</p>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {staffUsers.map((user) => (
            <Card key={user.id} className={`overflow-hidden ${!user.is_active ? 'opacity-60' : ''}`}>
              <CardHeader className="pb-3">
                <div className="flex items-start justify-between">
                  <div>
                    <CardTitle className="text-lg">{user.name}</CardTitle>
                    <p className="text-sm text-stone-500">@{user.username}</p>
                  </div>
                  <Badge variant={user.is_active ? 'default' : 'secondary'}>
                    {user.is_active ? 'نشط' : 'معطل'}
                  </Badge>
                </div>
              </CardHeader>
              <CardContent className="pt-0">
                <div className="flex items-center gap-2 text-sm text-stone-600 mb-4">
                  <Shield className="w-4 h-4" />
                  <span>{countPermissions(user.permissions)} صلاحية</span>
                </div>
                
                {/* Mini permission badges */}
                <div className="flex flex-wrap gap-1 mb-4">
                  {user.permissions?.view_production && (
                    <Badge variant="outline" className="text-xs">الإنتاج</Badge>
                  )}
                  {user.permissions?.view_customers && (
                    <Badge variant="outline" className="text-xs">الزبائن</Badge>
                  )}
                  {user.permissions?.view_orders && (
                    <Badge variant="outline" className="text-xs">الطلبات</Badge>
                  )}
                  {user.permissions?.view_expenses && (
                    <Badge variant="outline" className="text-xs">المصروفات</Badge>
                  )}
                  {user.permissions?.view_invoices && (
                    <Badge variant="outline" className="text-xs">الفواتير</Badge>
                  )}
                </div>

                {/* Work Schedule Section */}
                <div className="mb-4 p-3 bg-stone-50 rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-stone-700 flex items-center gap-1">
                      <Clock className="w-4 h-4" />
                      أوقات الدوام
                    </span>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => openScheduleDialog(user)}
                      className="h-7 text-xs"
                    >
                      <Calendar className="w-3 h-3 ml-1" />
                      إضافة
                    </Button>
                  </div>
                  {user.schedules && user.schedules.length > 0 ? (
                    <div className="space-y-1">
                      {user.schedules.map((schedule, idx) => (
                        <div key={schedule.id || idx} className="flex items-center justify-between text-xs bg-white p-2 rounded border">
                          <div className="flex items-center gap-2">
                            <span className="font-medium text-amber-700">{getDayLabel(schedule.day)}</span>
                            <span className="text-stone-600" dir="ltr">{schedule.start_time} - {schedule.end_time}</span>
                          </div>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-6 w-6 p-0 text-red-500 hover:text-red-700"
                            onClick={() => handleDeleteSchedule(user.id, schedule.id)}
                          >
                            <X className="w-3 h-3" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-xs text-stone-400 text-center py-2">لا توجد أوقات دوام محددة</p>
                  )}
                </div>

                <div className="flex items-center justify-between pt-3 border-t">
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-stone-500">
                      {user.is_active ? 'معطل' : 'مفعل'}
                    </span>
                    <Switch
                      checked={user.is_active}
                      onCheckedChange={() => handleToggleActive(user)}
                    />
                  </div>
                  <div className="flex gap-1">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleOpenDialog(user)}
                      data-testid={`edit-staff-${user.id}`}
                    >
                      <Edit className="w-4 h-4" />
                    </Button>
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button variant="ghost" size="icon" className="text-red-600 hover:text-red-700">
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>حذف المستخدم</AlertDialogTitle>
                          <AlertDialogDescription>
                            هل أنت متأكد من حذف "{user.name}"؟ لا يمكن التراجع عن هذا الإجراء.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>إلغاء</AlertDialogCancel>
                          <AlertDialogAction
                            onClick={() => handleDelete(user.id)}
                            className="bg-red-600 hover:bg-red-700"
                          >
                            حذف
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Schedule Dialog */}
      <Dialog open={scheduleDialogOpen} onOpenChange={setScheduleDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Clock className="w-5 h-5 text-amber-600" />
              إضافة وقت دوام
              {selectedUserForSchedule && (
                <span className="text-stone-500 font-normal text-sm">- {selectedUserForSchedule.name}</span>
              )}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>اليوم</Label>
              <Select value={scheduleForm.day} onValueChange={(v) => setScheduleForm({...scheduleForm, day: v})}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {daysOfWeek.map(day => (
                    <SelectItem key={day.value} value={day.value}>{day.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>وقت البدء</Label>
                <Input
                  type="time"
                  value={scheduleForm.start_time}
                  onChange={(e) => setScheduleForm({...scheduleForm, start_time: e.target.value})}
                />
              </div>
              <div className="space-y-2">
                <Label>وقت الانتهاء</Label>
                <Input
                  type="time"
                  value={scheduleForm.end_time}
                  onChange={(e) => setScheduleForm({...scheduleForm, end_time: e.target.value})}
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label>ملاحظات (اختياري)</Label>
              <Input
                value={scheduleForm.notes}
                onChange={(e) => setScheduleForm({...scheduleForm, notes: e.target.value})}
                placeholder="مثال: فترة صباحية"
              />
            </div>
          </div>
          
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setScheduleDialogOpen(false)}>إلغاء</Button>
            <Button onClick={handleAddSchedule} className="bg-amber-600 hover:bg-amber-700">
              <Bell className="w-4 h-4 ml-2" />
              إضافة وإشعار
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Wages Dialog */}
      <Dialog open={wagesDialogOpen} onOpenChange={setWagesDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Euro className="w-6 h-6 text-green-600" />
              حساب أجور الموظفين
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* Month/Year Selector */}
            <div className="flex items-center gap-4 p-4 bg-stone-50 rounded-lg">
              <div className="flex items-center gap-2">
                <Label>الشهر:</Label>
                <Select value={wagesMonth.toString()} onValueChange={(v) => setWagesMonth(parseInt(v))}>
                  <SelectTrigger className="w-32">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {[1,2,3,4,5,6,7,8,9,10,11,12].map(m => (
                      <SelectItem key={m} value={m.toString()}>
                        {new Date(2000, m-1).toLocaleDateString('ar', { month: 'long' })}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center gap-2">
                <Label>السنة:</Label>
                <Select value={wagesYear.toString()} onValueChange={(v) => setWagesYear(parseInt(v))}>
                  <SelectTrigger className="w-24">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {[2024, 2025, 2026, 2027].map(y => (
                      <SelectItem key={y} value={y.toString()}>{y}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <Button onClick={fetchWages} disabled={wagesLoading}>
                {wagesLoading ? 'جاري التحميل...' : 'عرض'}
              </Button>
            </div>

            {/* Wages Table */}
            {wagesData && (
              <>
                <div className="overflow-x-auto">
                  <table className="w-full border-collapse text-sm">
                    <thead>
                      <tr className="bg-stone-100">
                        <th className="p-2 text-right border">الموظف</th>
                        <th className="p-2 text-center border">أيام العمل</th>
                        <th className="p-2 text-center border">الغياب</th>
                        <th className="p-2 text-center border">الساعات</th>
                        <th className="p-2 text-center border">التأخير (دقيقة)</th>
                        <th className="p-2 text-center border">الأجر/ساعة</th>
                        <th className="p-2 text-center border">الراتب الأساسي</th>
                        <th className="p-2 text-center border text-red-600">خصم التأخير</th>
                        <th className="p-2 text-center border font-bold text-green-600">الصافي</th>
                      </tr>
                    </thead>
                    <tbody>
                      {wagesData.wages.map((w) => (
                        <tr key={w.staff_id} className="hover:bg-stone-50">
                          <td className="p-2 border font-medium">{w.staff_name}</td>
                          <td className="p-2 border text-center">{w.days_worked}</td>
                          <td className="p-2 border text-center">
                            {w.days_absent > 0 ? (
                              <span className="text-red-600 font-medium">{w.days_absent}</span>
                            ) : (
                              <span className="text-green-600">0</span>
                            )}
                          </td>
                          <td className="p-2 border text-center">{w.total_hours}</td>
                          <td className="p-2 border text-center">
                            {w.total_late_minutes > 0 ? (
                              <span className="text-amber-600 font-medium">{w.total_late_minutes}</span>
                            ) : (
                              <span className="text-green-600">0</span>
                            )}
                          </td>
                          <td className="p-2 border text-center">{w.hourly_rate}€</td>
                          <td className="p-2 border text-center">{w.total_wage}€</td>
                          <td className="p-2 border text-center text-red-600">
                            {w.late_deduction > 0 ? `-${w.late_deduction}€` : '-'}
                          </td>
                          <td className="p-2 border text-center font-bold text-green-600">{w.final_wage}€</td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr className="bg-green-50 font-bold">
                        <td colSpan={8} className="p-3 border text-left">المجموع الكلي:</td>
                        <td className="p-3 border text-center text-green-700 text-lg">{wagesData.total_wages}€</td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
                
                {/* Late Deduction Info */}
                <div className="mt-4 p-3 bg-amber-50 rounded-lg text-sm text-amber-800">
                  <p className="font-medium">💡 ملاحظة حول خصم التأخير:</p>
                  <p>يتم خصم 0.25 ساعة من الراتب عن كل 30 دقيقة تأخير</p>
                </div>
                
                {wagesData.wages.length === 0 && (
                  <p className="text-center text-stone-500 py-8">لا توجد سجلات حضور لهذا الشهر</p>
                )}
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default StaffUsersPage;
